import React, { useEffect, useState } from 'react';
import { Box, FormControlLabel, Grid, MenuItem, Stack, TextField } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { UserAdminMainSettingsDto } from '../../../../dtos/Users/UserAdminMainSettingsDto';
import TaskQueryDto from '../../../../dtos/TaskQueries/TaskQueryDto';
import TaskQueryApiService from '../../../../api/TaskQueryApiService';
import Section from '../../../Common/Section';
import { useSnackbar } from '../../../Common/SnackbarProvider';

export default function UserMainSettings (props) {
    const {
        userId, userAdminMainSettings, setUserAdminMainSettings, orgs, checkProjectUserXRefs, orgError, usernameError, passwordError, firstNameError,
        lastNameError, isCurrentUserSuperAdmin, userPassword, setUserPassword, confirmPassword, setConfirmPassword, editAdmin
    } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [possibleTaskQueries, setPossibleTaskQueries] = useState([]);

    const taskQueryApiService = new TaskQueryApiService();

    const handleFieldChange = (field, value) => {
        const updatedSettings = { ...userAdminMainSettings };
        updatedSettings[field] = value;
        setUserAdminMainSettings(updatedSettings);
    };

    async function getPossibleTaskQueries() {
        if (userAdminMainSettings.usOrg) {
            const response = await taskQueryApiService.getTaskQueriesForOrg(userAdminMainSettings.usOrg);
            if (response && response.data) {
                setPossibleTaskQueries(response.data);
            } else {
                snackbar.error(t('userMainSettings.toast.queriesError'));
            }
        }
    }

    useEffect(() => {
        void getPossibleTaskQueries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userAdminMainSettings]);

    const passwordsMismatch = userId === 0 && Boolean(userPassword) && Boolean(confirmPassword) && userPassword !== confirmPassword;

    return (
        <Box sx={{ pt: 1 }}>
            {/* ── Identity ───────────────────────────────────────────── */}
            {editAdmin && (
                <Section title={t('userMainSettings.section.identity')}>
                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            label={t('fieldName.organization')}
                            error={orgError}
                            value={userAdminMainSettings?.usOrg ?? ''}
                            onChange={(event) => {
                                checkProjectUserXRefs(event.target.value);
                                handleFieldChange('usOrg', event.target.value);
                            }}
                        >
                            {orgs.map((org) => (
                                <MenuItem key={org.ogId} value={org.ogId}>{org.ogName}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Section>
            )}

            {/* ── Credentials ────────────────────────────────────────── */}
            <Section title={t('userMainSettings.section.credentials')}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('userMainSettings.field.username')}
                        disabled={!editAdmin}
                        error={usernameError}
                        autoComplete='new-password'
                        value={userAdminMainSettings.usUsername ?? ''}
                        onChange={(event) => handleFieldChange('usUsername', event.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('fieldName.email')}
                        disabled={!editAdmin}
                        value={userAdminMainSettings.usEmail ?? ''}
                        onChange={(event) => handleFieldChange('usEmail', event.target.value)}
                    />
                </Grid>
                {userId === 0 && (
                    <>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('userMainSettings.field.password')}
                                type='password'
                                autoComplete='new-password'
                                error={passwordError}
                                value={userPassword ?? ''}
                                onChange={(event) => setUserPassword(event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('userMainSettings.field.confirmPassword')}
                                type='password'
                                autoComplete='off'
                                error={passwordError || passwordsMismatch}
                                helperText={passwordsMismatch ? t('userMainSettings.validation.passwordsMustMatch') : ''}
                                value={confirmPassword ?? ''}
                                onChange={(event) => setConfirmPassword(event.target.value)}
                            />
                        </Grid>
                    </>
                )}
            </Section>

            {/* ── Profile ────────────────────────────────────────────── */}
            <Section title={t('userMainSettings.section.profile')}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('userMainSettings.field.firstName')}
                        error={firstNameError}
                        value={userAdminMainSettings.usFirstname ?? ''}
                        onChange={(event) => handleFieldChange('usFirstname', event.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('userMainSettings.field.lastName')}
                        error={lastNameError}
                        value={userAdminMainSettings.usLastname ?? ''}
                        onChange={(event) => handleFieldChange('usLastname', event.target.value)}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label={t('userMainSettings.field.signature')}
                        value={userAdminMainSettings.usSignature ?? ''}
                        onChange={(event) => handleFieldChange('usSignature', event.target.value)}
                    />
                </Grid>
            </Section>

            {/* ── Preferences ────────────────────────────────────────── */}
            <Section title={t('userMainSettings.section.preferences')}>
                <Grid item xs={12} sm={8}>
                    <TextField
                        select
                        fullWidth
                        label={t('userMainSettings.field.defaultTaskQuery')}
                        disabled={!userAdminMainSettings?.usOrg}
                        value={userAdminMainSettings.usDefaultQuery ?? ''}
                        onChange={(event) => handleFieldChange('usDefaultQuery', event.target.value)}
                    >
                        {possibleTaskQueries.map((taskQuery) => (
                            <MenuItem key={taskQuery.queryId} value={taskQuery.queryId}>{taskQuery.queryName}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label={t('userMainSettings.field.tasksPerPage')}
                        type='number'
                        inputProps={{ min: 0 }}
                        value={userAdminMainSettings.usTasksPerPage ?? ''}
                        onChange={(event) => handleFieldChange('usTasksPerPage', event.target.value)}
                    />
                </Grid>
            </Section>

            {/* ── Flags ──────────────────────────────────────────────── */}
            {editAdmin && (
                <Section title={t('userMainSettings.section.flags')}>
                    <Grid item xs={12}>
                        <Stack direction="row" flexWrap="wrap" sx={{ rowGap: 0.5, columnGap: 3 }}>
                            {isCurrentUserSuperAdmin && (
                                <FormControlLabel
                                    control={<Checkbox checked={Boolean(userAdminMainSettings.usAdmin)} onChange={() => handleFieldChange('usAdmin', userAdminMainSettings.usAdmin ? 0 : 1)} />}
                                    label={t('userMainSettings.field.superAdmin')}
                                />
                            )}
                            {isCurrentUserSuperAdmin && (
                                <FormControlLabel
                                    control={<Checkbox checked={Boolean(userAdminMainSettings.isOrgAdmin)} onChange={() => handleFieldChange('isOrgAdmin', !userAdminMainSettings.isOrgAdmin)} />}
                                    label={t('userMainSettings.field.orgAdmin')}
                                />
                            )}
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(userAdminMainSettings.usActive)} onChange={() => handleFieldChange('usActive', !userAdminMainSettings.usActive)} />}
                                label={t('common.active')}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(userAdminMainSettings.usAddEnabled)} onChange={() => handleFieldChange('usAddEnabled', !userAdminMainSettings.usAddEnabled)} />}
                                label={t('userMainSettings.field.addEnabled')}
                            />
                        </Stack>
                    </Grid>
                </Section>
            )}
        </Box>
    );
}

UserMainSettings.propTypes = {
    userAdminMainSettings: PropTypes.objectOf(UserAdminMainSettingsDto),
    setUserAdminMainSettings: PropTypes.func.isRequired,
    orgs: PropTypes.array,
    userPassword: PropTypes.string,
    setUserPassword: PropTypes.func,
    confirmPassword: PropTypes.string,
    setConfirmPassword: PropTypes.func,
    checkProjectUserXRefs: PropTypes.func,
    orgError: PropTypes.bool,
    usernameError: PropTypes.bool,
    passwordError: PropTypes.bool,
    firstNameError: PropTypes.bool.isRequired,
    lastNameError: PropTypes.bool.isRequired,
    isCurrentUserSuperAdmin: PropTypes.bool.isRequired,
    editAdmin: PropTypes.bool.isRequired,
    taskQueries: PropTypes.arrayOf(TaskQueryDto),
    userId: PropTypes.number,
};
